import FieldValuesEditorView from "../../../entityFields/FieldValuesEditorView";
import type { FieldValuesState } from "../../../entityFields/FieldValuesContext";
import type { FieldValuesSectionProps } from "../../../entityFields/useFieldValuesEditor";
import type { ObjectCardEditChecks } from "../../../entityFields/useObjectCardChecks";
import type { InvoiceCardPreviewConfig } from "./invoiceCardPreviewState";
import type { InvoiceCardSource } from "./invoiceCardSource";
import InvoiceRelatedObjects from "./InvoiceRelatedObjects";
import { getFieldValueLabel, getLazyLookupOptionsType } from "./relatedObjectHelper";
import useInvoiceCardPreview from "./useInvoiceCardPreview";
import useInvoiceFieldValuesEditor from "./useInvoiceFieldValuesEditor";

interface Props extends FieldValuesSectionProps {
  cardPreview?: InvoiceCardPreviewConfig;
  source?: InvoiceCardSource;
  onEditChecksChange: (checks: ObjectCardEditChecks) => void;
}

const InvoiceFieldValuesSection = ({ cardPreview, source, onEditChecksChange, ...params }: Props) => {
  const { objectId, fields, cardLayout, fieldValues, lookupObjects, allowEditing } = params;

  const editor = useInvoiceFieldValuesEditor(params, source);

  const handleRowDefaults = (fieldIds: string[]) => {
    const previewedFields = editor.relatedObjectFieldsByObjectType[cardPreview?.relatedObjectType ?? ""] ?? [];
    const lazyOptionsTypes = previewedFields
      .filter((field) => fieldIds.includes(field.id))
      .map(getLazyLookupOptionsType);
    void editor.handleLookupOptionsNeeded(lazyOptionsTypes.filter((type) => type !== undefined));
  };

  const preview = useInvoiceCardPreview({
    config: cardPreview,
    objectId,
    storedFieldValues: fieldValues,
    fieldValues: editor.fieldValuesState.fieldValues,
    storedRelatedObjects: editor.relatedObjectsResp?.relatedObjectsByType,
    relatedObjectValuesState: editor.relatedObjectValuesState,
    setRelatedObjectValuesState: editor.setRelatedObjectValuesState,
    onFieldDefaults: editor.applyFieldValues,
    onRowDefaults: handleRowDefaults,
    onEditChecksChange,
  });

  const { fieldValuesState } = editor;

  const handleFieldValuesStateChangeReportingPreview = (stateUpdate: Partial<FieldValuesState>) => {
    if (stateUpdate.fieldValues && fieldValuesState.fieldEditState !== "canceling") {
      preview.reportUserChange(undefined, fieldValuesState.fieldValues, stateUpdate.fieldValues, (fieldId, value) =>
        getFieldValueLabel(
          fields,
          fieldId,
          value,
          editor.lookupOptionsByObjectType ?? {},
          lookupObjects?.[fieldId] ?? []
        )
      );
    }

    editor.handleFieldValuesStateChange(stateUpdate);
  };

  const handleCancelClickResettingPreview = () => {
    editor.handleCancelClick();
    preview.reset();
  };

  const editorReportingPreview = {
    ...editor,
    handleFieldValuesStateChange: handleFieldValuesStateChangeReportingPreview,
    handleCancelClick: handleCancelClickResettingPreview,
  };

  return (
    <FieldValuesEditorView
      {...params}
      editor={editorReportingPreview}
      isWaitingForValues={preview.isWaitingForDefaults}
      fieldsAutoFillTooltips={preview.fieldsAutoFillTooltips}
    >
      <InvoiceRelatedObjects
        editor={editorReportingPreview}
        cardLayout={cardLayout}
        allowEditing={allowEditing}
        invoiceFields={fields}
        cardPreview={cardPreview}
        sourceRelatedObjectType={source?.relatedObjectType}
        previewFieldValues={preview.fieldValues}
        cellDecorations={preview.cellDecorations}
        noOptionsTexts={preview.relatedNoOptionsTexts}
        hasPreviewFailed={preview.hasFailed}
        onUserRowChange={preview.reportUserChange}
      />
    </FieldValuesEditorView>
  );
};

export default InvoiceFieldValuesSection;
